import {
    type DocumentDownloadResponse,
    FetchApiError,
    type GetFufiInvestmentsResponse,
    type GetFundsPerformanceResponse,
    type GetLifeDetailResponse,
    type GetLifeDetailV2Response,
    type GetLifeTransactionsGetRequest,
    type GetLifeTransactionsResponse,
    ProductType,
} from "@repo/api";
import { type LifeContractType } from "@repo/logic";
import { useQuery, type UseQueryOptions } from "@tanstack/react-query";
import { ms } from "humanize-ms";
import { useContractTypeContext } from "../context/ContractTypeContext";
import { usePrimaryAPIClient } from "../context/PrimaryAPIClient";
import { useQueryKeyFactory } from "../context/QueryKeyProvider";
import { CustomApiError } from "../types/errors";

export type AcceptedLifeTypes = "CL" | "CLF";

export type LifeContractResponses = GetLifeDetailResponse | GetLifeDetailV2Response;

const AcceptedLifeTypes: AcceptedLifeTypes[] = [ProductType.Cl, ProductType.Clf];
type LifeDetailProps<TResult = GetLifeDetailResponse | GetLifeDetailV2Response> = {
    id: string;
    proposalNumber?: string | null;
    contractType?: AcceptedLifeTypes;
    options?: Partial<UseQueryOptions<LifeContractResponses, unknown, TResult>>;
};

// eslint-disable-next-line prettier/prettier
export const useLifeDetail = <
        TResult = GetLifeDetailResponse | GetLifeDetailV2Response,
    >({
    id,
    proposalNumber,
    contractType,
    options,
}: LifeDetailProps<TResult>) => {
    const { contractsLifeApi } = usePrimaryAPIClient();
    const { createQueryKey } = useQueryKeyFactory();
    const { contractType: contextContractType } = useContractTypeContext();
    const selectedContractType = (contractType ?? contextContractType) as AcceptedLifeTypes;

    if (!AcceptedLifeTypes.includes(selectedContractType)) {
        throw new Error(`Wrong type passed to useLifeDetail. Type: ${contractType}`);
    }

    const fetchMethod = {
        [ProductType.Cl]: () => contractsLifeApi.getLifeDetailGet({ contractId: id }),
        [ProductType.Clf]: () =>
            contractsLifeApi.getLifeDetailV2NdpGet({ contractId: id, proposalNumber: proposalNumber ?? undefined }),
    }[selectedContractType];

    const key = createQueryKey({
        key: ["getLifeDetail", id, selectedContractType, proposalNumber],
        includeUserIdentifier: false,
    });

    return useQuery<LifeContractResponses, unknown, TResult>({
        queryKey: key,
        queryFn: () => {
            return fetchMethod();
        },
        throwOnError: false,
        staleTime: ms("30m"), // fresh for 30 minutes
        gcTime: ms("30m"), // keep cache 30 minutes
        ...options,
    });
};

type ILifeFundsProps = {
    id: string;
    options?: UseQueryOptions<GetFundsPerformanceResponse[]>;
};

export const useLifeFunds = ({ id, options }: ILifeFundsProps) => {
    const { contractsLifeApi } = usePrimaryAPIClient();
    const { createQueryKey } = useQueryKeyFactory();

    return useQuery<GetFundsPerformanceResponse[]>({
        queryKey: createQueryKey({
            key: ["getLifeFunds", id],
            includeUserIdentifier: false,
        }),
        queryFn: () => {
            return contractsLifeApi.getLifeFundsGet({ contractId: id });
        },
        ...options,
    });
};

export type LifeTransactionsQueryParams = Pick<GetLifeTransactionsGetRequest, "count" | "to" | "from" | "typeFilter">;

type ILifeTransactionsProps = {
    contractId: string;
    contractType: LifeContractType;
    params: LifeTransactionsQueryParams;
    options?: Partial<UseQueryOptions<GetLifeTransactionsResponse[]>>;
};

export const transactionsPerRequest = 60;

export const isUnsupportedFufiContractTypeError = (error: unknown, contractType: unknown = ProductType.Clf) => {
    if (contractType !== ProductType.Clf) return false;
    return error instanceof FetchApiError && error.errorResponse.code === CustomApiError.MethodNotAllowed;
};

export const useLifeTransactions = ({ contractId, contractType, params, options }: ILifeTransactionsProps) => {
    const { contractsLifeApi } = usePrimaryAPIClient();
    const { createQueryKey } = useQueryKeyFactory();

    return useQuery({
        queryKey: createQueryKey({
            key: ["getLifeTransactions", contractId, contractType, params],
            includeUserIdentifier: false,
        }),
        queryFn: async () => {
            return await contractsLifeApi.getLifeTransactionsGet({
                contractId,
                count: params.count,
                from: params.from,
                to: params.to,
                productType: contractType,
                typeFilter: params.typeFilter,
            });
        },
        ...options,
    });
};

type ILifeDocumentsProps = {
    id: string;
    options?: Partial<UseQueryOptions<DocumentDownloadResponse[]>>;
};

export const useLifeDocuments = ({ id, options }: ILifeDocumentsProps) => {
    const { documentsApi } = usePrimaryAPIClient();
    const { createQueryKey } = useQueryKeyFactory();

    return useQuery<DocumentDownloadResponse[]>({
        queryKey: createQueryKey({
            key: ["getLifeDocuments", id],
            includeUserIdentifier: false,
        }),
        queryFn: () => {
            return documentsApi.getLifeDocumentsGet({ contractId: id });
        },
        ...options,
    });
};

type ILifeFufiInvestmentsProps = {
    id: string;
    options?: Partial<UseQueryOptions<GetFufiInvestmentsResponse>>;
};

export const useLifeFufiInvestments = ({ id, options }: ILifeFufiInvestmentsProps) => {
    const { contractsLifeApi } = usePrimaryAPIClient();
    const { createQueryKey } = useQueryKeyFactory();

    return useQuery<GetFufiInvestmentsResponse>({
        queryKey: createQueryKey({
            key: ["getLifeFufiInvestments", id],
            includeUserIdentifier: true,
        }),
        queryFn: async () => {
            return await contractsLifeApi.getLifeFufiInvestmentsGet({ contractId: id });
        },
        ...options,
    });
};
